import { useState } from "react";
import {
  Plus,
  Pen,
  Trash2,
  Eye,
  Users,
  MapPin,
  ArrowLeft,
  AlertTriangle,
  Search,
  X,
} from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/components/ui/table";
import { Switch } from "@shared/components/ui/switch";
import { Textarea } from "@shared/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@shared/components/ui/alert-dialog";
import { Checkbox } from "@shared/components/ui/checkbox";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { DEFAULT_VALUES } from "@shared/utils/constants";
import { SETTINGS_TOAST_KEYS } from "@features/settings/constants";
import {
  useCustomPricingsQuery,
  useCreateCustomPricing,
  useUpdateCustomPricing,
  useDeleteCustomPricing,
} from "@features/settings/hooks/useCustomPricing";
import { SelectCity } from "@/features/settings/citys/SelectCity";
import { SelectUser } from "@/features/users/SelectUser";
import { MultiSelectUser } from "@/features/users/components";
import { MultiSelectCity } from "@shared/components/MultiSelectCity";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";

export function CustomPricingManagement() {
  const { user } = useAuth();
  const { t } = useTranslation(["settings", "toast", "common"]);
  const [searchParams] = useState({
    page: 1,
    limit: 10000,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [filterCity, setFilterCity] = useState("");

  const { data, isLoading, error } = useCustomPricingsQuery(searchParams);
  const { mutate: createCustomPricing, isPending: isCreating } =
    useCreateCustomPricing();
  const { mutate: updateCustomPricing, isPending: isUpdating } =
    useUpdateCustomPricing();
  const { mutate: deleteCustomPricing, isPending: isDeleting } =
    useDeleteCustomPricing();

  const [selectedPricing, setSelectedPricing] = useState(null);
  const [currentView, setCurrentView] = useState("list"); // "list", "create", "edit", "view", "bulk-create"

  const [formData, setFormData] = useState({
    user: "",
    city: "",
    deliveryFees: DEFAULT_VALUES.DELIVERY_FEES,
    refusalFees: DEFAULT_VALUES.REFUSAL_FEES,
    cancellationFees: DEFAULT_VALUES.CANCELLATION_FEES,
    deadline: DEFAULT_VALUES.DEADLINE,
    isActive: true,
    notes: "",
  });

  const [bulkFormData, setBulkFormData] = useState({
    users: [],
    cities: [],
    deliveryFees: DEFAULT_VALUES.DELIVERY_FEES,
    refusalFees: DEFAULT_VALUES.REFUSAL_FEES,
    cancellationFees: DEFAULT_VALUES.CANCELLATION_FEES,
    deadline: DEFAULT_VALUES.DEADLINE,
    isActive: true,
    notes: "",
  });

  const resetForm = () => {
    setFormData({
      user: "",
      city: "",
      deliveryFees: DEFAULT_VALUES.DELIVERY_FEES,
      refusalFees: DEFAULT_VALUES.REFUSAL_FEES,
      cancellationFees: DEFAULT_VALUES.CANCELLATION_FEES,
      deadline: DEFAULT_VALUES.DEADLINE,
      isActive: true,
      notes: "",
    });
    setSelectedPricing(null);
    setCurrentView("list");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDeleteMultiple = () => {
    if (selectedItems.length === 0) {
      toast.error("Please select items to delete");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${
          selectedItems.length
        } custom pricing configuration${selectedItems.length > 1 ? "s" : ""}?`
      )
    ) {
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    selectedItems.forEach((id) => {
      deleteCustomPricing(id, {
        onSuccess: () => {
          successCount++;
          if (successCount + errorCount === selectedItems.length) {
            if (successCount === selectedItems.length) {
              toast.success(
                `Successfully deleted ${successCount} custom pricing configuration${
                  successCount > 1 ? "s" : ""
                }`
              );
            } else {
              toast.warning(
                `Deleted ${successCount} out of ${selectedItems.length} configurations`
              );
            }
            setSelectedItems([]);
          }
        },
        onError: () => {
          errorCount++;
          if (successCount + errorCount === selectedItems.length) {
            if (successCount > 0) {
              toast.warning(
                `Deleted ${successCount} out of ${selectedItems.length} configurations`
              );
            } else {
              toast.error("Failed to delete custom pricing configurations");
            }
            setSelectedItems([]);
          }
        },
      });
    });
  };

  const toggleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === filteredData?.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredData?.map((item) => item._id) || []);
    }
  };

  // Filter data based on search query and city filter
  const filteredData = data?.data?.filter((pricing) => {
    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const userName = (
        pricing.user?.fullName ||
        pricing.user?.brandName ||
        ""
      ).toLowerCase();
      const userEmail = (pricing.user?.email || "").toLowerCase();
      const cityName = (pricing.city?.name || "").toLowerCase();
      const cityArabic = (pricing.city?.nameArabic || "").toLowerCase();

      const matchesSearch =
        userName.includes(query) ||
        userEmail.includes(query) ||
        cityName.includes(query) ||
        cityArabic.includes(query);

      if (!matchesSearch) return false;
    }

    // City filter
    if (filterCity && pricing.city?._id !== filterCity) {
      return false;
    }

    return true;
  });

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    createCustomPricing(formData, {
      onSuccess: () => {
        toast.success(t("toast:" + SETTINGS_TOAST_KEYS.PRICING_CREATED));
        resetForm();
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.message ||
            t("toast:" + SETTINGS_TOAST_KEYS.PRICING_CREATE_ERROR)
        );
      },
    });
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    updateCustomPricing(
      {
        id: selectedPricing._id,
        data: formData,
      },
      {
        onSuccess: () => {
          toast.success(t("toast:" + SETTINGS_TOAST_KEYS.PRICING_UPDATED));
          resetForm();
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.message ||
              t("toast:" + SETTINGS_TOAST_KEYS.PRICING_UPDATE_ERROR)
          );
        },
      }
    );
  };

  const handleDelete = (id) => {
    if (window.confirm(t("common:confirmDelete"))) {
      deleteCustomPricing(id, {
        onSuccess: () => {
          toast.success(t("toast:" + SETTINGS_TOAST_KEYS.PRICING_DELETED));
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.message || "Failed to delete custom pricing"
          );
        },
      });
    }
  };

  const openEditView = (pricing) => {
    setSelectedPricing(pricing);
    setFormData({
      user: pricing.user?._id || "",
      city: pricing.city?._id || "",
      deliveryFees: pricing.deliveryFees,
      refusalFees: pricing.refusalFees,
      cancellationFees: pricing.cancellationFees,
      deadline: pricing.deadline,
      isActive: pricing.isActive,
      notes: pricing.notes || "",
    });
    setCurrentView("edit");
  };

  const openViewDetails = (pricing) => {
    setSelectedPricing(pricing);
    setCurrentView("view");
  };

  if (user?.role !== "admin") {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">
            Access denied. Admin privileges required.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Create Form View
  if (currentView === "create") {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              Create Custom Pricing
            </h2>
            <p className="text-muted-foreground">
              Set custom pricing for a specific user and city combination
            </p>
          </div>
          <Button variant="outline" onClick={() => setCurrentView("list")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to List
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Custom Pricing Form</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="user">User</Label>
                  <SelectUser
                    user={formData.user}
                    onSelectUser={(userId) => handleInputChange("user", userId)}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <SelectCity
                    city={formData.city}
                    onSelectCity={(city) => handleInputChange("city", city._id)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="deliveryFees">Delivery Fees</Label>
                  <Input
                    id="deliveryFees"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.deliveryFees}
                    onChange={(e) =>
                      handleInputChange(
                        "deliveryFees",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="refusalFees">Refusal Fees</Label>
                  <Input
                    id="refusalFees"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.refusalFees}
                    onChange={(e) =>
                      handleInputChange(
                        "refusalFees",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cancellationFees">Cancellation Fees</Label>
                  <Input
                    id="cancellationFees"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.cancellationFees}
                    onChange={(e) =>
                      handleInputChange(
                        "cancellationFees",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="deadline">Deadline</Label>
                  <Input
                    id="deadline"
                    value={formData.deadline}
                    onChange={(e) =>
                      handleInputChange("deadline", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="isActive">Active</Label>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="isActive"
                      checked={formData.isActive}
                      onCheckedChange={(checked) =>
                        handleInputChange("isActive", checked)
                      }
                    />
                    <Label htmlFor="isActive">
                      {formData.isActive ? "Active" : "Inactive"}
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  placeholder="Additional notes or comments..."
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentView("list")}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreating}>
                  {isCreating ? "Creating..." : "Create"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Bulk Create Form View
  if (currentView === "bulk-create") {
    const handleBulkInputChange = (field, value) => {
      setBulkFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const handleBulkCreateSubmit = async () => {
      // Create custom pricing for each user-city combination
      const combinations = [];
      bulkFormData.users.forEach((user) => {
        bulkFormData.cities.forEach((city) => {
          combinations.push({
            user: user._id,
            city: city._id,
            deliveryFees: bulkFormData.deliveryFees,
            refusalFees: bulkFormData.refusalFees,
            cancellationFees: bulkFormData.cancellationFees,
            deadline: bulkFormData.deadline,
            isActive: bulkFormData.isActive,
            notes: bulkFormData.notes,
          });
        });
      });

      if (combinations.length === 0) {
        toast.error(t("settings:selectUserAndCity"));
        return;
      }

      // Check for existing combinations to avoid duplicates
      const existingCombinations = data?.data || [];
      const duplicates = combinations.filter((combo) =>
        existingCombinations.some(
          (existing) =>
            existing.user._id === combo.user && existing.city._id === combo.city
        )
      );

      if (duplicates.length > 0) {
        toast.warning(
          `${duplicates.length} combination${
            duplicates.length > 1 ? "s" : ""
          } already exist and will be skipped`
        );
        console.warn("Duplicate combinations found:", duplicates);
      }

      // Filter out duplicates
      const uniqueCombinations = combinations.filter(
        (combo) =>
          !existingCombinations.some(
            (existing) =>
              existing.user._id === combo.user &&
              existing.city._id === combo.city
          )
      );

      if (uniqueCombinations.length === 0) {
        toast.error("All selected combinations already exist");
        return;
      }

      // Show loading state
      const toastId = toast.loading(
        `Creating ${uniqueCombinations.length} custom pricing configurations...`
      );

      try {
        let successCount = 0;
        let errorCount = 0;
        const errors = [];

        // Process combinations with better error handling
        for (const [index, pricing] of uniqueCombinations.entries()) {
          try {
            await new Promise((resolve, reject) => {
              createCustomPricing(pricing, {
                onSuccess: () => {
                  successCount++;
                  resolve();
                },
                onError: (error) => {
                  errorCount++;
                  errors.push({
                    user:
                      bulkFormData.users.find((u) => u._id === pricing.user)
                        ?.fullName ||
                      bulkFormData.users.find((u) => u._id === pricing.user)
                        ?.brandName,
                    city: bulkFormData.cities.find(
                      (c) => c._id === pricing.city
                    )?.name,
                    error: error.response?.data?.message || error.message,
                  });
                  reject(error);
                },
              });
            });
          } catch (error) {
            // Error already handled in the onError callback
            console.error(
              `Failed to create pricing for combination ${index + 1}:`,
              error
            );
          }
        }

        // Dismiss loading toast
        toast.dismiss(toastId);

        // Show results
        if (successCount === uniqueCombinations.length) {
          toast.success(
            `Successfully created all ${uniqueCombinations.length} custom pricing configurations!`
          );
          // Reset form and go back to list
          setBulkFormData({
            users: [],
            cities: [],
            deliveryFees: 0,
            refusalFees: 0,
            cancellationFees: 0,
            deadline: "24/48",
            isActive: true,
            notes: "",
          });
          setCurrentView("list");
        } else if (successCount > 0) {
          toast.warning(
            `Created ${successCount} out of ${uniqueCombinations.length} configurations. ${errorCount} failed.`
          );
          // Show detailed errors
          if (errors.length > 0) {
            console.error("Bulk creation errors:", errors);
            const errorMessage = errors
              .slice(0, 3)
              .map((err) => `${err.user} - ${err.city}: ${err.error}`)
              .join("\n");
            toast.error(
              `Some configurations failed:\n${errorMessage}${
                errors.length > 3 ? "\n..." : ""
              }`
            );
          }
        } else {
          toast.error("Failed to create any custom pricing configurations");
          if (errors.length > 0) {
            console.error("Bulk creation errors:", errors);
          }
        }
      } catch (error) {
        toast.dismiss(toastId);
        toast.error("An unexpected error occurred during bulk creation");
        console.error("Bulk creation error:", error);
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              Bulk Create Custom Pricing
            </h2>
            <p className="text-muted-foreground">
              Create custom pricing for multiple user-city combinations
            </p>
          </div>
          <Button variant="outline" onClick={() => setCurrentView("list")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to List
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Bulk Custom Pricing Form</CardTitle>
            <p className="text-sm text-muted-foreground">
              Select multiple users and cities to create custom pricing for all
              combinations
            </p>
          </CardHeader>
          <CardContent>
            <form className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="users">Users</Label>
                  <MultiSelectUser
                    users={bulkFormData.users}
                    onSelect={(users) => handleBulkInputChange("users", users)}
                    placeholder="Select users..."
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    {bulkFormData.users.length} user
                    {bulkFormData.users.length !== 1 ? "s" : ""} selected
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cities">Cities</Label>
                  <MultiSelectCity
                    cities={bulkFormData.cities}
                    onSelect={(cities) =>
                      handleBulkInputChange("cities", cities)
                    }
                    placeholder="Select cities..."
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    {bulkFormData.cities.length} cit
                    {bulkFormData.cities.length !== 1 ? "ies" : "y"} selected
                  </p>
                </div>
              </div>

              {bulkFormData.users.length > 0 &&
                bulkFormData.cities.length > 0 && (
                  <div className="space-y-4">
                    <div className="bg-muted/50 p-4 rounded-lg border-2 border-dashed border-muted-foreground/20">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-foreground">
                          Preview:{" "}
                          {bulkFormData.users.length *
                            bulkFormData.cities.length}{" "}
                          configurations will be created
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {bulkFormData.users.length} ×{" "}
                          {bulkFormData.cities.length}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Each selected user will get custom pricing for each
                        selected city
                      </p>
                    </div>

                    {/* Preview Table for first few combinations */}
                    <div className="border rounded-lg">
                      <div className="p-3 border-b bg-muted/30">
                        <h4 className="text-sm font-medium">
                          Preview (first 5 combinations)
                        </h4>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">User</TableHead>
                              <TableHead className="text-xs">City</TableHead>
                              <TableHead className="text-xs">
                                Delivery Fee
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {bulkFormData.users.slice(0, 5).map((user) =>
                              bulkFormData.cities.slice(0, 1).map((city) => (
                                <TableRow key={`${user._id}-${city._id}`}>
                                  <TableCell className="text-xs">
                                    {user.fullName || user.brandName}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {city.name}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {bulkFormData.deliveryFees} DH
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                            {bulkFormData.users.length *
                              bulkFormData.cities.length >
                              5 && (
                              <TableRow>
                                <TableCell
                                  colSpan={3}
                                  className="text-center text-xs text-muted-foreground py-2"
                                >
                                  ... and{" "}
                                  {bulkFormData.users.length *
                                    bulkFormData.cities.length -
                                    5}{" "}
                                  more combinations
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                )}

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="deliveryFees">Delivery Fees</Label>
                  <Input
                    id="deliveryFees"
                    type="number"
                    min="0"
                    step="0.01"
                    value={bulkFormData.deliveryFees}
                    onChange={(e) =>
                      handleBulkInputChange(
                        "deliveryFees",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="refusalFees">Refusal Fees</Label>
                  <Input
                    id="refusalFees"
                    type="number"
                    min="0"
                    step="0.01"
                    value={bulkFormData.refusalFees}
                    onChange={(e) =>
                      handleBulkInputChange(
                        "refusalFees",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cancellationFees">Cancellation Fees</Label>
                  <Input
                    id="cancellationFees"
                    type="number"
                    min="0"
                    step="0.01"
                    value={bulkFormData.cancellationFees}
                    onChange={(e) =>
                      handleBulkInputChange(
                        "cancellationFees",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="deadline">Deadline</Label>
                  <Input
                    id="deadline"
                    value={bulkFormData.deadline}
                    onChange={(e) =>
                      handleBulkInputChange("deadline", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="isActive">Active</Label>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="isActive"
                      checked={bulkFormData.isActive}
                      onCheckedChange={(checked) =>
                        handleBulkInputChange("isActive", checked)
                      }
                    />
                    <Label htmlFor="isActive">
                      {bulkFormData.isActive ? "Active" : "Inactive"}
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={bulkFormData.notes}
                  onChange={(e) =>
                    handleBulkInputChange("notes", e.target.value)
                  }
                  placeholder="Additional notes or comments..."
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentView("list")}
                >
                  Cancel
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      disabled={
                        bulkFormData.users.length === 0 ||
                        bulkFormData.cities.length === 0
                      }
                    >
                      Create{" "}
                      {bulkFormData.users.length * bulkFormData.cities.length}{" "}
                      Configurations
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        Confirm Bulk Creation
                      </AlertDialogTitle>
                      <AlertDialogDescription className="space-y-3">
                        <p>
                          You are about to create{" "}
                          <strong>
                            {bulkFormData.users.length *
                              bulkFormData.cities.length}
                          </strong>{" "}
                          custom pricing configurations.
                        </p>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="font-medium">
                              Selected Users ({bulkFormData.users.length}):
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground">
                              {bulkFormData.users.slice(0, 3).map((user) => (
                                <li key={user._id} className="truncate">
                                  {user.fullName || user.brandName}
                                </li>
                              ))}
                              {bulkFormData.users.length > 3 && (
                                <li>
                                  ...and {bulkFormData.users.length - 3} more
                                </li>
                              )}
                            </ul>
                          </div>

                          <div>
                            <p className="font-medium">
                              Selected Cities ({bulkFormData.cities.length}):
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground">
                              {bulkFormData.cities.slice(0, 3).map((city) => (
                                <li key={city._id} className="truncate">
                                  {city.name}
                                </li>
                              ))}
                              {bulkFormData.cities.length > 3 && (
                                <li>
                                  ...and {bulkFormData.cities.length - 3} more
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>

                        <div className="bg-muted p-3 rounded-lg">
                          <p className="text-sm font-medium">
                            Pricing Details:
                          </p>
                          <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground mt-1">
                            <span>
                              Delivery: {bulkFormData.deliveryFees} DH
                            </span>
                            <span>Refusal: {bulkFormData.refusalFees} DH</span>
                            <span>
                              Cancellation: {bulkFormData.cancellationFees} DH
                            </span>
                          </div>
                        </div>

                        <p className="text-sm text-amber-600">
                          This action cannot be undone. Are you sure you want to
                          proceed?
                        </p>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleBulkCreateSubmit}
                        disabled={isCreating}
                        className="bg-primary hover:bg-primary/90"
                      >
                        {isCreating ? "Creating..." : "Yes, Create All"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Edit Form View
  if (currentView === "edit") {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              Edit Custom Pricing
            </h2>
            <p className="text-muted-foreground">
              Update the custom pricing configuration
            </p>
          </div>
          <Button variant="outline" onClick={() => setCurrentView("list")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to List
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Custom Pricing Form</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleEditSubmit} className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="deliveryFees">Delivery Fees</Label>
                  <Input
                    id="deliveryFees"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.deliveryFees}
                    onChange={(e) =>
                      handleInputChange(
                        "deliveryFees",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="refusalFees">Refusal Fees</Label>
                  <Input
                    id="refusalFees"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.refusalFees}
                    onChange={(e) =>
                      handleInputChange(
                        "refusalFees",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cancellationFees">Cancellation Fees</Label>
                  <Input
                    id="cancellationFees"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.cancellationFees}
                    onChange={(e) =>
                      handleInputChange(
                        "cancellationFees",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="deadline">Deadline</Label>
                  <Input
                    id="deadline"
                    value={formData.deadline}
                    onChange={(e) =>
                      handleInputChange("deadline", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="isActive">Active</Label>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="isActive"
                      checked={formData.isActive}
                      onCheckedChange={(checked) =>
                        handleInputChange("isActive", checked)
                      }
                    />
                    <Label htmlFor="isActive">
                      {formData.isActive ? "Active" : "Inactive"}
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  placeholder="Additional notes or comments..."
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentView("list")}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isUpdating}>
                  {isUpdating ? "Updating..." : "Update"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // View Details View
  if (currentView === "view" && selectedPricing) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              Custom Pricing Details
            </h2>
            <p className="text-muted-foreground">
              View custom pricing configuration
            </p>
          </div>
          <Button variant="outline" onClick={() => setCurrentView("list")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to List
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Pricing Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">User</Label>
                  <p className="text-sm">
                    {selectedPricing.user?.fullName ||
                      selectedPricing.user?.brandName ||
                      "N/A"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {selectedPricing.user?.email || ""}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium">City</Label>
                  <p className="text-sm">
                    {selectedPricing.city?.name || "N/A"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {selectedPricing.city?.nameArabic || ""}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium">Delivery Fees</Label>
                  <p className="text-sm">{selectedPricing.deliveryFees} DH</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Refusal Fees</Label>
                  <p className="text-sm">{selectedPricing.refusalFees} DH</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">
                    Cancellation Fees
                  </Label>
                  <p className="text-sm">
                    {selectedPricing.cancellationFees} DH
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Deadline</Label>
                  <p className="text-sm">{selectedPricing.deadline}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <Badge
                    variant={selectedPricing.isActive ? "default" : "secondary"}
                  >
                    {selectedPricing.isActive ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>

              {selectedPricing.notes && (
                <div>
                  <Label className="text-sm font-medium">Notes</Label>
                  <p className="text-sm text-muted-foreground">
                    {selectedPricing.notes}
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => openEditView(selectedPricing)}
                >
                  <Pen className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(selectedPricing._id)}
                  disabled={isDeleting}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // List View (Default)
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            Custom Pricing Management
          </h2>
          <p className="text-muted-foreground">
            Manage custom pricing for specific users and cities
          </p>
        </div>

        <div className="flex space-x-2">
          <Button onClick={() => setCurrentView("create")}>
            <Plus className="mr-2 h-4 w-4" />
            Add Custom Pricing
          </Button>
          <Button
            variant="outline"
            onClick={() => setCurrentView("bulk-create")}
          >
            <Users className="mr-2 h-4 w-4" />
            Bulk Create
          </Button>
        </div>
      </div>

      {/* Search and Actions Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="flex-1 max-w-sm">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by user, email, or city..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1 h-7 w-7 p-0"
                      onClick={() => setSearchQuery("")}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="w-64">
                <SelectCity
                  city={filterCity}
                  onSelectCity={(city) => setFilterCity(city?._id || "")}
                  placeholder="Filter by city..."
                  showAllOption={true}
                />
              </div>

              {(searchQuery || filterCity) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchQuery("");
                    setFilterCity("");
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>

            {selectedItems.length > 0 && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-sm">
                  {selectedItems.length} selected
                </Badge>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Selected
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        Confirm Delete
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete {selectedItems.length}{" "}
                        custom pricing configuration
                        {selectedItems.length > 1 ? "s" : ""}? This action
                        cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteMultiple}
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Custom Pricings Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Custom Pricings</CardTitle>
            {filteredData && (
              <Badge variant="outline">
                {filteredData.length}{" "}
                {filteredData.length === 1 ? "item" : "items"}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <ThreeDotLoader />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              Error loading custom pricings: {error.message}
            </div>
          ) : filteredData?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery || filterCity
                ? "No custom pricings found matching the selected filters"
                : "No custom pricings found"}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={
                        selectedItems.length === filteredData?.length &&
                        filteredData?.length > 0
                      }
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Delivery Fees</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData?.map((pricing) => (
                  <TableRow key={pricing._id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedItems.includes(pricing._id)}
                        onCheckedChange={() => toggleSelectItem(pricing._id)}
                        aria-label="Select row"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">
                            {pricing.user?.fullName ||
                              pricing.user?.brandName ||
                              "N/A"}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {pricing.user?.email || ""}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div>{pricing.city?.name || "N/A"}</div>
                          <div className="text-sm text-muted-foreground">
                            {pricing.city?.nameArabic || ""}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{pricing.deliveryFees} DH</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={pricing.isActive ? "default" : "secondary"}
                      >
                        {pricing.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openViewDetails(pricing)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openEditView(pricing)}
                        >
                          <Pen className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="icon"
                          onClick={() => handleDelete(pricing._id)}
                          disabled={isDeleting}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
